import React, { Dispatch, SetStateAction, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { SignUpInfoMobile } from "./SignUpInfoMobile";
import { SignUpConfirmMobile } from "./SignUpConfirmMobile";
import Logo from "../../assets/Logo.svg";
import { useIntl } from "react-intl";
import I18nKey from "translations/I18nKey";
import { apiClient } from "api";
import { useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SignUpProps } from "./types";
import classNames from "classnames";
import useLoginSignupStyles from "./styles";
import { ConfirmResendVerify } from "./ConfirmResendVerify";
import DialogBox from "components/DialogBox";
import SimpleButton from "components/SimpleButton";

import { DialogBoxEvent, SignUpError } from "types";

interface SignUpModalInterface {
  isModalOpen: boolean;
  handleModalClose: () => void;
  setSignupLoading: Dispatch<SetStateAction<boolean>>;
  setIsSuccess: Dispatch<SetStateAction<boolean>>;
  setAlertMsg: Dispatch<SetStateAction<string>>;
  signupLoading: boolean;
  inputFocus: boolean;
  handleSignIn: (userName: string, password: string) => void;
}

export const SignUpModal: React.FC<SignUpModalInterface> = ({
  isModalOpen,
  handleModalClose,
  setIsSuccess,
  setSignupLoading,
  signupLoading,
  handleSignIn,
  setAlertMsg,
  inputFocus,
}) => {
  const [isOTPSend, setIsOTPSend] = useState<boolean>(false);
  const [singUpErr, setSingUpErr] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showConfirmResend, setShowConfirmResend] = useState<boolean>(false);
  const [showConfirmCloseModal, setShowConfirmCloseModal] =
    useState<boolean>(false);
  const [showNotVerifiedText, setShowNotVerifiedText] =
    useState<boolean>(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const intl = useIntl();
  const classes = useLoginSignupStyles();
  const getSignUpInitData = async (signUpPayload: SignUpProps) => {
    const userInfo: string =
      (signUpPayload.phone_no
        ? signUpPayload.phone_no
        : signUpPayload.email_id) || "";
    setSignupLoading(true);
    setUserName(userInfo);
    setPassword(signUpPayload.password);
    await apiClient
      .userSignup(signUpPayload)
      .then((res) => {
        if (res?.data.data) {
          setIsOTPSend(true);
          setSingUpErr("");
        } else if (res.config.data.data.errors) {
          setSingUpErr(res.config.data.data.errors.message);
        }
      })
      .catch((err) => {
        const error = err.response.data.errors;
        setSingUpErr(error.message);
        if (error.exists !== SignUpError.USER && !error.is_verified)
          setShowConfirmResend(true);
      })
      .finally(() => {
        setSignupLoading(false);
      });
  };

  const signUpErrorAlert = (
    message: string = intl.formatMessage({
      id: I18nKey.INVALID_EMAIL_PASSWORD,
    })
  ) => {
    setSingUpErr(message);
    message = "";
  };

  const handleClose = (_event?: any, reason?: string) => {
    if (reason !== DialogBoxEvent.BACKDROP) {
      setShowConfirmCloseModal(true);
    }
  };

  return (
    <>
      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        data-tut="reactour__signUp"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            maxWidth: isMobile ? null : "500px",
            display: "flex-start",
          },
        }}
      >
        <DialogTitle className={classNames(classes.dialogTitle)}>
          <Box className={classNames(classes.signupImage)}>
            <img src={Logo}></img>
            <span
              className={classNames(classes.signupClose)}
              onClick={handleClose}
            >
              <CloseIcon />
            </span>
          </Box>
        </DialogTitle>
        <DialogContent className={classNames(classes.dialogContent)}>
          <>
            {!isOTPSend && !showConfirmResend ? (
              <SignUpInfoMobile
                handleModalClose={handleModalClose}
                getSignupInitData={getSignUpInitData}
                setUserName={setUserName}
                setSingUpErr={setSingUpErr}
                signUpErrorAlert={signUpErrorAlert}
                singUpErr={singUpErr}
                signupLoading={signupLoading}
              />
            ) : showConfirmResend && !isOTPSend ? (
              <ConfirmResendVerify
                userName={userName}
                setShowConfirmResend={setShowConfirmResend}
                setIsOTPSend={setIsOTPSend}
              />
            ) : (
              <SignUpConfirmMobile
                userName={userName}
                password={password}
                handleModalClose={handleModalClose}
                setIsSuccess={setIsSuccess}
                setSignupLoading={setSignupLoading}
                handleSignIn={handleSignIn}
                setAlertMsg={setAlertMsg}
              />
            )}
          </>
        </DialogContent>
      </Dialog>

      {showConfirmCloseModal && (
        <DialogBox
          isModalOpen={showConfirmCloseModal}
          handleModalClose={() => setShowConfirmCloseModal(false)}
          content={
            <>
              <Grid container rowSpacing={2}>
                <Grid item xs={12}>
                  <Box className={classNames(classes.exitSignupContainer)}>
                    <Typography className={classNames(classes.createAccount)}>
                      {isOTPSend
                        ? intl.formatMessage({
                            id: I18nKey.EXIT_VERIFICATION,
                          })
                        : intl.formatMessage({
                            id: I18nKey.EXIT_SIGNUP,
                          })}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className={classNames(classes.exitSignUpBtnsHolder)}>
                    <SimpleButton
                      text={intl.formatMessage({
                        id: I18nKey.BTN_NO,
                      })}
                      onClick={() => setShowConfirmCloseModal(false)}
                      className={classNames(classes.signUpBtnInfo)}
                    />
                    <SimpleButton
                      primary
                      text={intl.formatMessage({
                        id: I18nKey.BTN_YES,
                      })}
                      disable={signupLoading}
                      onClick={handleModalClose}
                      className={classNames(classes.cancelBtn)}
                    />
                  </Box>
                </Grid>
              </Grid>
            </>
          }
        />
      )}
    </>
  );
};
